import * as React from "react";
import Chunk from "./Chunk";
import { cx } from "utils/join-classnames";
import {
    listSegments,
    listNextSegments,
    listPreviousSegments,
    listSegmentsByTimestamp,
} from "core/services";
import { Segment, Selection, Channel, RelativeTimestamp } from "core/models";
import { Button, LoaderContainer } from "ui/components";
import { useSessionContext } from "ui/context/SessionContext";
import { useEditorContext } from "ui/context/EditorContext";
import styles from "./ChunkContainer.module.scss";

const CHUNK_HEIGHT = 81;
const CHUNK_WIDTH = 150;
const REFRESH_INTERVAL = 5000; // Interval for the auto-refesh in MS

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    channelID: number;
    height: number;
    previewPlayerTime: number;
    savingClip: boolean;
    channel: Channel;
    onAutoplay(autoplay: boolean | "end"): void;
}

const ChunkContainer: React.FC<Props> = ({
    channelID,
    height,
    previewPlayerTime,
    savingClip,
    className,
    channel,
    onAutoplay,
}) => {
    const sessionContext = useSessionContext();
    const editorContext = useEditorContext();
    const { selections, setSelections, currentSelectionID } = editorContext;

    const [dimensions, setDimensions] = React.useState<{
        rows: number;
        cols: number;
    }>({ rows: 0, cols: 0 });

    const [loading, setLoading] = React.useState<boolean>(false);
    const [columnOffset, setColumnOffset] = React.useState<number>(0);
    const [segments, setSegments] = React.useState<Segment[]>([]);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const refreshTimoutRef = React.useRef<ReturnType<
        typeof setInterval
    > | null>(null);
    const [noNextColumns, setNoNextColumns] = React.useState<boolean>(false);
    const [noPrevColumns, setNoPrevColumns] = React.useState<boolean>(false);
    const [dateStart, setDateStart] = React.useState<Date>(new Date());

    // The start index is 0 + the number of columns we need to skip to achieve our current 'view'
    const numberOfVisibleSegments = dimensions.cols * dimensions.rows;
    const startIndex = columnOffset * dimensions.rows;
    const endIndex = startIndex + numberOfVisibleSegments;
    const visibleSegments = segments.slice(startIndex, endIndex);

    React.useEffect(() => {
        const checkDateEnd = new Date(Date.now() - 1000 * 20);
        const checkDateStart = new Date(dateStart.getTime() + 1000 * 60 * 5);
        setNoNextColumns(
            editorContext.chunkContainerDisplayDate.getTime() >=
                checkDateEnd.getTime(),
        );
        setNoPrevColumns(
            editorContext.chunkContainerDisplayDate.getTime() <=
                checkDateStart.getTime(),
        );
    }, [editorContext.chunkContainerDisplayDate, dateStart]);

    React.useEffect(() => {
        const start = new Date(
            new Date().getTime() - channel.exStream.onlineMinutes * 60000,
        );
        setDateStart(start);
    }, [channel]);

    const selection = selections.find((x) => x.id === currentSelectionID);

    const setSelection = (newSelection: Selection) => {
        if (selections.length === 0) {
            setSelections([newSelection]);
            return;
        }
        const index = selections.findIndex((x) => x.id === currentSelectionID);
        const newSelections = [...selections];
        newSelections[index] = newSelection;
        setSelections(newSelections);
    };

    function calculateSize() {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const { offsetWidth, offsetHeight } = container;

        const cols = Math.floor(offsetWidth / CHUNK_WIDTH);
        const rows = Math.floor(offsetHeight / CHUNK_HEIGHT);
        setDimensions({
            rows,
            cols,
        });
    }

    const loadSegments = React.useCallback(async () => {
        if (!sessionContext.session) {
            return;
        }
        setLoading(true);
        const newSegments = await listSegments(
            sessionContext.session.token,
            channelID,
            0,
            200,
        );
        setColumnOffset(0);
        editorContext.setChunkContainerNavigationDate(undefined);
        editorContext.setChunkContainerDisplayDate(new Date());
        if (newSegments.value) {
            setSegments(newSegments.value.reverse());
        }
        setLoading(false);
    }, [editorContext, channelID, sessionContext.session?.token]);

    const autoLoadSegments = React.useCallback(() => {
        if (!editorContext.liveMode) {
            return;
        }
        loadSegments();
    }, [editorContext.liveMode, loadSegments]);
    const autoRefreshLoop = React.useCallback(() => {
        if (!editorContext.liveMode) {
            return;
        }
        autoLoadSegments();
        refreshTimoutRef.current = setTimeout(
            autoRefreshLoop,
            REFRESH_INTERVAL,
        );
    }, [editorContext.liveMode, autoLoadSegments]);
    React.useEffect(() => {
        if (refreshTimoutRef.current) {
            clearTimeout(refreshTimoutRef.current);
        }
        refreshTimoutRef.current = null;
        if (!editorContext.liveMode) {
            return;
        }
        autoRefreshLoop();
    }, [editorContext.liveMode]);

    React.useEffect(() => {
        editorContext.setLiveMode(false);
    }, [savingClip]);

    const loadSegmentsByDate = async (date: Date) => {
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            setLoading(true);
            editorContext.setLiveMode(false);
            const newSegments = await listSegmentsByTimestamp(
                sessionContext.session.token,
                channelID,
                Math.round(date.getTime() / 1000),
                20,
                140,
            );
            if (newSegments.value) {
                setSegments(newSegments.value.reverse());
            }
            setSelection({
                ...(selection || new Selection()),
                segments: [],
                startSegmentID: "",
                endSegmentID: "",
            });
        } catch (err) {
            console.warn(err);
        }
        setColumnOffset(0);

        setLoading(false);
    };

    React.useEffect(calculateSize, [loading]);
    React.useEffect(() => {
        const lastSegment = segments[columnOffset * dimensions.rows];
        if (!lastSegment) {
            return;
        }
        editorContext.setChunkContainerDisplayDate(
            new Date(lastSegment.timestamp * 1000),
        );
    }, [columnOffset, segments, dimensions.rows]);

    React.useEffect(() => {
        if (!editorContext.chunkContainerNavigationDate) {
            return;
        }
        loadSegmentsByDate(editorContext.chunkContainerNavigationDate);
    }, [editorContext.chunkContainerNavigationDate]);

    React.useEffect(() => {
        loadSegments();
    }, [channelID]);
    React.useEffect(calculateSize, [height]);

    React.useEffect(() => {
        const container = containerRef.current;
        if (container) {
            window.addEventListener("resize", calculateSize);
        }
        return () => {
            if (refreshTimoutRef.current) {
                clearTimeout(refreshTimoutRef.current);
            }
            refreshTimoutRef.current = null;
            window.removeEventListener("resize", calculateSize);
        };
    }, []);

    const checkOrLoadAvailableColumns = React.useCallback(
        async (columnMoveCount: number): Promise<number> => {
            if (!sessionContext.session) {
                return 0;
            }
            if (loading) {
                return 0;
            }
            let actualColumnMoveCount = columnMoveCount;
            const neededSegments = Math.abs(columnMoveCount) * dimensions.rows;

            // Negative moves go for 'newer' segments
            if (columnMoveCount < 0) {
                let missingSegments = neededSegments - startIndex;

                // If we still have space we can simply return the requested column move count
                if (missingSegments < 0) {
                    return columnMoveCount;
                }
                setLoading(true);

                // Otherwise we need to fetch more columns
                // We normalize the amount as a multiple of the rows
                // we need and then fetch twice that
                missingSegments += missingSegments % dimensions.rows;
                missingSegments *= 6;

                const newSegmentsResponse = await listNextSegments(
                    sessionContext.session.token,
                    channelID,
                    segments[0].number,
                    missingSegments,
                );

                // Need to reverse the newSegments as the newest are last in that list
                const newSegments = newSegmentsResponse.value?.reverse() || [];

                const newSegmentsState = newSegments.concat(segments);
                setSegments(newSegmentsState);

                // Figure out how many columns we got and whether we can actually make the move:
                const movableColumns = Math.floor(
                    newSegments.length / columnMoveCount,
                );

                // In case we simply don't have enough, attempt to move as much as we possibly can
                if (movableColumns < columnMoveCount) {
                    actualColumnMoveCount = movableColumns;
                }
                setLoading(false);
                return actualColumnMoveCount;
            }
            const newEndIndex = endIndex + neededSegments;
            if (newEndIndex > segments.length) {
                setLoading(true);

                const numberOfMissingSegments =
                    newEndIndex - segments.length + 1;
                const segmentsToList = numberOfMissingSegments * 6;

                const newSegments = await listPreviousSegments(
                    sessionContext.session.token,
                    channelID,
                    segments[segments.length - 1].number,
                    segmentsToList,
                );
                const newSegmentsList = segments.concat(
                    newSegments.value?.reverse() || [],
                );
                setSegments(newSegmentsList);

                // Figure out how many columns we got and whether we can actually make the move:
                const movableColumns = newSegments.value
                    ? Math.floor(newSegments.value.length / columnMoveCount)
                    : 0;

                // In case we simply don't have enough, attempt to move as much as we possibly can
                if (movableColumns < columnMoveCount) {
                    actualColumnMoveCount = movableColumns;
                }

                setLoading(false);
                return actualColumnMoveCount;
            }

            return columnMoveCount;
        },
        [
            dimensions.rows,
            endIndex,
            loading,
            channelID,
            segments,
            sessionContext.session?.token,
            startIndex,
        ],
    );

    const navigateColumnsNext = React.useCallback(
        async (columnMoveCount = 1) => {
            const availableColumnMoveCount = await checkOrLoadAvailableColumns(
                -1 * columnMoveCount,
            );
            let realColumnMoveCount = availableColumnMoveCount;
            if (Math.abs(availableColumnMoveCount) > columnMoveCount) {
                realColumnMoveCount = -1 * columnMoveCount;
            }
            let newOffset = columnOffset + realColumnMoveCount;
            if (newOffset < 0) {
                newOffset = 0;
            }
            setColumnOffset(newOffset);
        },
        [checkOrLoadAvailableColumns, columnOffset],
    );
    const navigateColumnsPrevious = React.useCallback(
        async (columnMoveCount = 1) => {
            const availableColumnMoveCount =
                await checkOrLoadAvailableColumns(columnMoveCount);
            let realColumnMoveCount = availableColumnMoveCount;
            if (availableColumnMoveCount > columnMoveCount) {
                realColumnMoveCount = columnMoveCount;
            }
            const newOffset = columnOffset + realColumnMoveCount;
            setColumnOffset(newOffset);
        },
        [checkOrLoadAvailableColumns, columnOffset],
    );

    const generateSelectionList = (
        startSegmentID: string,
        endSegmentID: string,
    ): { segmentMap: Map<string, RelativeTimestamp>; segments: Segment[] } => {
        // @TODO: Would a singular for loop finding both indexes be faster?
        //        this could be profiled to find out.
        const endIDIndex = segments.findIndex((x) => x.id === endSegmentID);
        const startIDIndex = segments.findIndex((x) => x.id === startSegmentID);

        // In case our start and end are the same we're simply selecting 1 segment
        if (startSegmentID === endSegmentID) {
            const list = new Map<string, RelativeTimestamp>();
            list.set(startSegmentID, 0);
            return {
                segments: [segments[startIDIndex]],
                segmentMap: list,
            };
        }
        const list = new Map<string, RelativeTimestamp>();

        // Incorrect selections simply return an empty list
        // these usually indicate an attempt to deselect
        if (endIDIndex > startIDIndex) {
            return {
                segments: [],
                segmentMap: list,
            };
        }

        let totalDuration = 0;
        for (let i = startIDIndex; i >= endIDIndex; i -= 1) {
            const segment = segments[i];
            list.set(segment.id, totalDuration);
            totalDuration += segment.tsLength;
        }

        return {
            segments: segments.slice(endIDIndex, startIDIndex + 1),
            segmentMap: list,
        };
    };

    const handleChunkLeftHalfClick = React.useCallback(
        (segmentID: string) => {
            editorContext.setLiveMode(false);
            const newSelection: Selection = {
                ...(selection || new Selection()),
                startSegmentID: segmentID,
                trimStart: 0,
                trimEnd: 0,
                mediaFile: undefined,
            };
            if (!newSelection.endSegmentID) {
                newSelection.endSegmentID = segmentID;
            }
            const list = generateSelectionList(
                newSelection.startSegmentID,
                newSelection.endSegmentID,
            );
            if (list.segments.length === 0) {
                newSelection.startSegmentID = "";
                newSelection.endSegmentID = "";
            }
            newSelection.segments = list.segments;
            newSelection.segmentMap = list.segmentMap;
            if (newSelection.id) {
                setSelection(newSelection);
            }
            onAutoplay(true);
        },
        [selection, generateSelectionList],
    );
    const handleChunkRightHalfClick = React.useCallback(
        (segmentID: string) => {
            editorContext.setLiveMode(false);
            const newSelection = {
                ...(selection || new Selection()),
                trimStart: 0,
                trimEnd: 0,
                endSegmentID: segmentID,
                mediaFile: undefined,
            };
            if (!newSelection.startSegmentID) {
                newSelection.startSegmentID = segmentID;
            }
            const list = generateSelectionList(
                newSelection.startSegmentID,
                newSelection.endSegmentID,
            );
            if (list.segments.length === 0) {
                newSelection.startSegmentID = "";
                newSelection.endSegmentID = "";
            }
            newSelection.segments = list.segments;
            newSelection.segmentMap = list.segmentMap;
            onAutoplay("end");
            setSelection(newSelection);
        },
        [selection, generateSelectionList],
    );

    const navigateColumnsPrevSmall = React.useCallback(() => {
        navigateColumnsPrevious(1);
        editorContext.setLiveMode(false);
    }, [navigateColumnsPrevious]);
    const navigateColumnsPrevMedium = React.useCallback(() => {
        navigateColumnsPrevious(4);
        editorContext.setLiveMode(false);
    }, [navigateColumnsPrevious]);
    const navigateColumnsPrevLarge = React.useCallback(() => {
        navigateColumnsPrevious(20);
        editorContext.setLiveMode(false);
    }, [navigateColumnsPrevious]);
    const navigateColumnsNextSmall = React.useCallback(() => {
        navigateColumnsNext(1);
        editorContext.setLiveMode(false);
    }, [navigateColumnsNext]);
    const navigateColumnsNextMedium = React.useCallback(() => {
        navigateColumnsNext(4);
        editorContext.setLiveMode(false);
    }, [navigateColumnsNext]);
    const navigateColumnsNextLarge = React.useCallback(() => {
        navigateColumnsNext(20);
        editorContext.setLiveMode(false);
    }, [navigateColumnsNext]);

    return (
        <div className={cx(styles.container, className)} ref={containerRef}>
            <div className={styles.navigation__buttons}>
                <div className={styles.navigation__buttons__top} />
                <div className={styles.navigation__buttons__bottom}>
                    <Button
                        type="function"
                        theme="light"
                        icon="arrow-left"
                        onClick={navigateColumnsPrevSmall}
                        className={styles.button}
                        disabled={noPrevColumns}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="arrow-left-double"
                        onClick={navigateColumnsPrevMedium}
                        className={styles.button}
                        disabled={noPrevColumns}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="arrow-left-triple"
                        onClick={navigateColumnsPrevLarge}
                        className={styles.button}
                        disabled={noPrevColumns}
                    />
                </div>
            </div>
            <div className={styles.chunk__container__container}>
                <div className={styles.chunk__container}>
                    {visibleSegments.map((segment) => {
                        const isSelectionStart =
                            selection &&
                            segment.id === selection.startSegmentID;
                        const isSelectionEnd =
                            selection && segment.id === selection.endSegmentID;
                        const relativeTimestamp = selection?.segmentMap.get(
                            segment.id,
                        );
                        const isSelected =
                            selection && selection.segmentMap.has(segment.id);
                        let isPlaying = false;
                        if (
                            relativeTimestamp &&
                            previewPlayerTime >= relativeTimestamp &&
                            previewPlayerTime <
                                relativeTimestamp + segment.tsLength
                        ) {
                            isPlaying = true;
                        }
                        let position: "in" | "before" | "after" = "in";
                        if (
                            selection &&
                            segment.timestamp < selection.segments[0]?.timestamp
                        ) {
                            position = "before";
                        }
                        if (
                            selection &&
                            selection.segments.length !== 0 &&
                            segment.timestamp >
                                selection.segments[
                                    selection.segments.length - 1
                                ].timestamp
                        ) {
                            position = "after";
                        }

                        return (
                            <Chunk
                                key={segment.id}
                                channelID={channelID}
                                segment={segment}
                                showDuration={editorContext.showDurations}
                                showTimestamp={editorContext.showTimestamps}
                                isPlaying={isPlaying}
                                isSelectionStart={isSelectionStart || false}
                                isSelectionEnd={isSelectionEnd || false}
                                selected={isSelected || false}
                                onClickLeft={handleChunkLeftHalfClick}
                                onClickRight={handleChunkRightHalfClick}
                                position={position}
                            />
                        );
                    })}
                    {loading && <LoaderContainer message="Loading segments" />}
                </div>
            </div>
            <div className={styles.navigation__buttons}>
                <div className={styles.navigation__buttons__top} />
                <div className={styles.navigation__buttons__bottom}>
                    <Button
                        type="function"
                        theme="light"
                        icon="arrow-right"
                        onClick={navigateColumnsNextSmall}
                        className={styles.button}
                        disabled={noNextColumns}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="arrow-right-double"
                        onClick={navigateColumnsNextMedium}
                        className={styles.button}
                        disabled={noNextColumns}
                    />
                    <Button
                        type="function"
                        theme="light"
                        icon="arrow-right-triple"
                        onClick={navigateColumnsNextLarge}
                        className={styles.button}
                        disabled={noNextColumns}
                    />
                </div>
            </div>
        </div>
    );
};

export default React.memo(ChunkContainer);
